import React, { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Building2,
  Car,
  Loader2,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Plus,
  Receipt,
  Search,
  Trash2,
} from 'lucide-react'
import { Button } from '../../components/ui'
import { Card, CardContent, Badge } from '../../components/ui'
import { ModernButton, ModernInput } from '../../components/ui'
import { PhoneInput } from '../../components/form'
import { useBranches, Branch } from '../../hooks/useBranches'
import { lookupZipCode } from '../../services/cepLookup'
import { digitsOnly, formatCep } from '../../utils/brFormat'
import { cn } from '../../utils/cn'

const INPUT_DS =
  'h-11 rounded-xl px-4 py-3 text-base md:text-sm border-input shadow-sm placeholder:text-muted-foreground'

function branchPhoneDisplayToE164(display: string): string | undefined {
  let d = digitsOnly(display)
  if (!d) return undefined
  if (d.startsWith('55') && d.length > 2) d = d.slice(2)
  if (d.length < 10 || d.length > 11) return undefined
  return `+55${d}`
}

function e164ToBranchPhoneDisplay(phone?: string): string {
  if (!phone) return ''
  let d = digitsOnly(phone)
  if (d.startsWith('55')) d = d.slice(2)
  if (d.length < 10) return ''
  const ac = d.slice(0, 2)
  const rest = d.slice(2)
  if (rest.length <= 8) {
    return `(${ac}) ${rest.slice(0, 4)}-${rest.slice(4, 8)}`
  }
  return `(${ac}) ${rest.slice(0, 5)}-${rest.slice(5, 11)}`
}

const DEFAULT_BRANCH_CATEGORIES = ['A', 'B', 'C', 'D'] as const

const VEHICLE_CATEGORY_DEFS = [
  { value: 'A', icon: '🏍️', labelKey: 'settings.dealership.branch.categoryLabels.A' as const },
  { value: 'B', icon: '🚗', labelKey: 'settings.dealership.branch.categoryLabels.B' as const },
  { value: 'C', icon: '🚛', labelKey: 'settings.dealership.branch.categoryLabels.C' as const },
  { value: 'D', icon: '🚌', labelKey: 'settings.dealership.branch.categoryLabels.D' as const },
]

type BranchFormData = Omit<Branch, 'id' | 'createdAt' | 'phone'> & {
  phoneDisplay: string
}

const emptyBranchForm = (): BranchFormData => ({
  name: '',
  email: '',
  phoneDisplay: '',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
  zipCode: '',
  isActive: true,
  isPrimary: false,
  allowedVehicleCategories: [...DEFAULT_BRANCH_CATEGORIES],
  stateRegistration: '',
  taxRegime: '',
  fiscalDefaultNcm: '',
  fiscalDefaultCfop: '',
})

export const DealershipSettingsPage: React.FC = () => {
  const { t } = useTranslation()
  const branchPhoneFieldId = useId()

  const { branches, isLoading: loadingBranches, error: branchError, createBranch, updateBranch, deleteBranch } = useBranches()
  const [showBranchForm, setShowBranchForm] = useState(false)
  const [editingBranchId, setEditingBranchId] = useState<string | null>(null)
  const [branchForm, setBranchForm] = useState<BranchFormData>(emptyBranchForm())
  const [branchOpError, setBranchOpError] = useState<string | null>(null)

  const [isLoadingZipCode, setIsLoadingZipCode] = useState(false)

  const normalizeBranchCategories = (branch: Branch): string[] => {
    const raw = branch.allowedVehicleCategories
    if (!raw || raw.length === 0) return [...DEFAULT_BRANCH_CATEGORIES]
    return [...raw]
  }

  const toggleFormCategory = (value: string) => {
    setBranchForm(f => {
      const current = f.allowedVehicleCategories || [...DEFAULT_BRANCH_CATEGORIES]
      const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value]
      return { ...f, allowedVehicleCategories: next }
    })
  }

  const openNewBranchForm = () => {
    setEditingBranchId(null)
    setBranchForm(emptyBranchForm())
    setBranchOpError(null)
    setShowBranchForm(true)
  }

  const openEditBranchForm = (branch: Branch) => {
    setEditingBranchId(branch.id)
    setBranchForm({
      name: branch.name,
      email: branch.email || '',
      phoneDisplay: e164ToBranchPhoneDisplay(branch.phone),
      street: branch.street,
      number: branch.number,
      complement: branch.complement || '',
      neighborhood: branch.neighborhood,
      city: branch.city,
      state: (branch.state || '').toUpperCase().slice(0, 2),
      zipCode: formatCep(branch.zipCode || ''),
      isActive: branch.isActive,
      isPrimary: branch.isPrimary,
      allowedVehicleCategories: normalizeBranchCategories(branch),
      stateRegistration: (branch.stateRegistration ?? '').trim(),
      taxRegime: (branch.taxRegime ?? '').trim(),
      fiscalDefaultNcm: (branch.fiscalDefaultNcm ?? '').trim(),
      fiscalDefaultCfop: (branch.fiscalDefaultCfop ?? '').trim(),
    })
    setBranchOpError(null)
    setShowBranchForm(true)
  }

  const cancelBranchForm = () => {
    setShowBranchForm(false)
    setEditingBranchId(null)
    setBranchForm(emptyBranchForm())
    setBranchOpError(null)
  }

  const handleBranchSubmit = async () => {
    if (isLoadingZipCode) return
    setBranchOpError(null)
    const requiredChecks: Array<{ ok: boolean; label: string }> = [
      { ok: Boolean(branchForm.name?.trim()), label: t('settings.dealership.branch.form.fields.name') },
      { ok: digitsOnly(branchForm.zipCode).length === 8, label: t('settings.dealership.branch.form.fields.zipCode') },
      { ok: Boolean(branchForm.street?.trim()), label: t('settings.dealership.branch.form.fields.street') },
      { ok: Boolean(branchForm.number?.trim()), label: t('settings.dealership.branch.form.fields.number') },
      { ok: Boolean(branchForm.neighborhood?.trim()), label: t('settings.dealership.branch.form.fields.neighborhood') },
      { ok: Boolean(branchForm.city?.trim()), label: t('settings.dealership.branch.form.fields.city') },
      { ok: branchForm.state.trim().length === 2, label: t('settings.dealership.branch.form.fields.state') },
    ]
    const missing = requiredChecks.find((c) => !c.ok)
    if (missing) {
      setBranchOpError(t('settings.dealership.branch.form.requiredFieldError', { field: missing.label }))
      return
    }

    if (branchForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(branchForm.email)) {
      setBranchOpError(t('settings.dealership.branch.form.invalidEmail'))
      return
    }

    const catList = branchForm.allowedVehicleCategories || []
    if (catList.length < 1) {
      setBranchOpError(t('settings.dealership.branch.form.categoriesRequired'))
      return
    }

    const phoneE164 = branchPhoneDisplayToE164(branchForm.phoneDisplay)
    if (digitsOnly(branchForm.phoneDisplay).length > 0 && !phoneE164) {
      setBranchOpError(t('settings.dealership.branch.form.invalidPhone'))
      return
    }

    const payload: Omit<Branch, 'id' | 'createdAt'> = {
      name: branchForm.name.trim(),
      email: branchForm.email?.trim() || undefined,
      phone: phoneE164,
      street: branchForm.street.trim(),
      number: branchForm.number.trim(),
      complement: branchForm.complement?.trim() || undefined,
      neighborhood: branchForm.neighborhood.trim(),
      city: branchForm.city.trim(),
      state: branchForm.state.trim().toUpperCase(),
      zipCode: digitsOnly(branchForm.zipCode),
      isActive: branchForm.isActive,
      isPrimary: branchForm.isPrimary,
      allowedVehicleCategories: catList,
      stateRegistration: branchForm.stateRegistration?.trim() || undefined,
      taxRegime: branchForm.taxRegime?.trim() || undefined,
      fiscalDefaultNcm: branchForm.fiscalDefaultNcm?.trim() || undefined,
      fiscalDefaultCfop: branchForm.fiscalDefaultCfop?.trim() || undefined,
    }

    try {
      if (editingBranchId) {
        await updateBranch(editingBranchId, payload)
      } else {
        await createBranch(payload)
      }
      cancelBranchForm()
    } catch (err: any) {
      setBranchOpError(err?.message || t('settings.dealership.branch.form.saveError'))
    }
  }

  const lookupBranchZipCode = async () => {
    setBranchOpError(null)
    const normalizedZipCode = digitsOnly(branchForm.zipCode)
    if (normalizedZipCode.length !== 8) {
      setBranchOpError(t('settings.dealership.branch.form.zipCodeIncomplete'))
      return
    }
    try {
      setIsLoadingZipCode(true)
      const address = await lookupZipCode(normalizedZipCode, { publicOnly: false })
      setBranchForm(previous => ({
        ...previous,
        zipCode: address.zipCode ? formatCep(address.zipCode) : previous.zipCode,
        street: address.street || previous.street,
        neighborhood: address.neighborhood || previous.neighborhood,
        city: address.city || previous.city,
        state: (address.state || previous.state || '').toUpperCase().slice(0, 2),
        complement: previous.complement || address.complement || '',
      }))
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : t('settings.dealership.branch.form.zipCodeLookupError')
      setBranchOpError(msg)
    } finally {
      setIsLoadingZipCode(false)
    }
  }

  const handleDeleteBranch = async (branch: Branch) => {
    if (branch.isPrimary) return
    if (!window.confirm(`Remover a filial "${branch.name}"?`)) return
    try {
      await deleteBranch(branch.id)
    } catch {
      void 0
    }
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          {t('settings.dealership.pageTitle')}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('settings.dealership.pageSubtitle')}
        </p>
      </div>

      <div className="space-y-6 pt-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 pb-2 border-b border-border flex-1">
                <Building2 className="h-4 w-4 text-accent" />
                <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  {t('settings.dealership.branch.sectionTitle')}
                </span>
              </div>
              <Button type="button" onClick={openNewBranchForm} className="ml-4">
                <Plus className="h-4 w-4" />
                {t('settings.dealership.branch.newButton')}
              </Button>
            </div>

            {showBranchForm && (
              <Card className="rounded-2xl border-border shadow-sm">
                <CardContent className="relative p-6 space-y-8 overflow-hidden rounded-2xl">
                  {isLoadingZipCode ? (
                    <div
                      className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-background/75 backdrop-blur-[2px]"
                      role="status"
                      aria-live="polite"
                      aria-busy="true"
                    >
                      <Loader2 className="h-9 w-9 animate-spin text-accent" aria-hidden />
                      <p className="text-sm font-medium text-foreground">{t('settings.dealership.branch.form.searchingZipCode')}</p>
                    </div>
                  ) : null}

                  <div className={cn(isLoadingZipCode && 'pointer-events-none select-none opacity-50')}>
                  <h3 className="text-lg font-semibold text-foreground tracking-tight">
                    {editingBranchId ? t('settings.dealership.branch.form.editTitle') : t('settings.dealership.branch.form.newTitle')}
                  </h3>

                  <div className="space-y-4">
                    <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                      {t('settings.dealership.branch.form.sectionBranch')}
                    </p>
                    <ModernInput
                      className={INPUT_DS}
                      label={t('settings.dealership.branch.form.fields.name')}
                      value={branchForm.name}
                      onChange={e => setBranchForm(f => ({ ...f, name: e.target.value }))}
                      placeholder={t('settings.dealership.branch.form.placeholders.name')}
                      disabled={isLoadingZipCode}
                    />
                  </div>

                  <div className="space-y-4">
                    <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                      {t('settings.dealership.branch.form.sectionContact')}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label htmlFor={branchPhoneFieldId} className="block text-sm font-medium text-foreground">
                          {t('settings.dealership.branch.form.fields.phone')}
                        </label>
                        <PhoneInput
                          id={branchPhoneFieldId}
                          value={branchForm.phoneDisplay}
                          onChange={v => setBranchForm(f => ({ ...f, phoneDisplay: v }))}
                          className={`w-full ${INPUT_DS}`}
                          disabled={isLoadingZipCode}
                        />
                        <p className="text-xs text-muted-foreground">{t('settings.dealership.branch.form.phoneHint')}</p>
                      </div>
                      <ModernInput
                        className={INPUT_DS}
                        label={t('settings.dealership.branch.form.fields.email')}
                        type="email"
                        autoComplete="email"
                        value={branchForm.email || ''}
                        onChange={e => setBranchForm(f => ({ ...f, email: e.target.value }))}
                        placeholder={t('settings.dealership.branch.form.placeholders.email')}
                        disabled={isLoadingZipCode}
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                      {t('settings.dealership.branch.form.sectionAddress')}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
                      <div className="sm:col-span-2 lg:col-span-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-3">
                        <div className="min-w-0 flex-1 sm:max-w-[14rem]">
                          <ModernInput
                            className={INPUT_DS}
                            label={t('settings.dealership.branch.form.fields.zipCode')}
                            value={branchForm.zipCode}
                            onChange={e => setBranchForm(f => ({ ...f, zipCode: formatCep(e.target.value) }))}
                            onKeyDown={e => {
                              if (e.key === 'Enter') {
                                e.preventDefault()
                                void lookupBranchZipCode()
                              }
                            }}
                            placeholder="00000-000"
                            inputMode="numeric"
                            autoComplete="postal-code"
                            disabled={isLoadingZipCode}
                          />
                        </div>
                        <ModernButton
                          type="button"
                          variant="outline"
                          size="md"
                          loading={isLoadingZipCode}
                          disabled={isLoadingZipCode}
                          className="h-11 shrink-0 rounded-xl"
                          onClick={() => void lookupBranchZipCode()}
                        >
                          <span className="inline-flex items-center gap-2">
                            {!isLoadingZipCode ? <Search className="h-4 w-4 shrink-0" aria-hidden /> : null}
                            {t('settings.dealership.branch.form.zipSearchButton')}
                          </span>
                        </ModernButton>
                      </div>
                      <p className="sm:col-span-2 lg:col-span-6 text-xs text-muted-foreground -mt-2">
                        {t('settings.dealership.branch.form.zipSearchHint')}
                      </p>
                      <div className="sm:col-span-2 lg:col-span-4">
                        <ModernInput
                          className={INPUT_DS}
                          label={t('settings.dealership.branch.form.fields.street')}
                          value={branchForm.street}
                          onChange={e => setBranchForm(f => ({ ...f, street: e.target.value }))}
                          placeholder={t('settings.dealership.branch.form.placeholders.street')}
                          autoComplete="address-line1"
                          disabled={isLoadingZipCode}
                        />
                      </div>
                      <div className="sm:col-span-2 lg:col-span-2">
                        <ModernInput
                          className={INPUT_DS}
                          label={t('settings.dealership.branch.form.fields.number')}
                          value={branchForm.number}
                          onChange={e => setBranchForm(f => ({ ...f, number: e.target.value }))}
                          placeholder={t('settings.dealership.branch.form.placeholders.number')}
                          maxLength={20}
                          autoComplete="address-line2"
                          disabled={isLoadingZipCode}
                        />
                      </div>
                      <div className="sm:col-span-2 lg:col-span-3 max-w-md">
                        <ModernInput
                          className={INPUT_DS}
                          label={t('settings.dealership.branch.form.fields.complement')}
                          value={branchForm.complement || ''}
                          onChange={e => setBranchForm(f => ({ ...f, complement: e.target.value }))}
                          placeholder={t('settings.dealership.branch.form.placeholders.complement')}
                          maxLength={120}
                          autoComplete="off"
                          disabled={isLoadingZipCode}
                        />
                      </div>
                      <div className="sm:col-span-2 lg:col-span-3">
                        <ModernInput
                          className={INPUT_DS}
                          label={t('settings.dealership.branch.form.fields.neighborhood')}
                          value={branchForm.neighborhood}
                          onChange={e => setBranchForm(f => ({ ...f, neighborhood: e.target.value }))}
                          placeholder={t('settings.dealership.branch.form.placeholders.neighborhood')}
                          autoComplete="address-level3"
                          disabled={isLoadingZipCode}
                        />
                      </div>
                      <div className="sm:col-span-2 lg:col-span-4">
                        <ModernInput
                          className={INPUT_DS}
                          label={t('settings.dealership.branch.form.fields.city')}
                          value={branchForm.city}
                          onChange={e => setBranchForm(f => ({ ...f, city: e.target.value }))}
                          placeholder={t('settings.dealership.branch.form.placeholders.city')}
                          autoComplete="address-level2"
                          disabled={isLoadingZipCode}
                        />
                      </div>
                      <div className="sm:col-span-2 lg:col-span-2 max-w-[8.5rem]">
                        <ModernInput
                          className={INPUT_DS}
                          label={t('settings.dealership.branch.form.fields.state')}
                          value={branchForm.state}
                          onChange={e =>
                            setBranchForm(f => ({
                              ...f,
                              state: e.target.value.replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, 2),
                            }))
                          }
                          placeholder="SP"
                          maxLength={2}
                          autoComplete="address-level1"
                          disabled={isLoadingZipCode}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 pt-6 border-t border-border">
                    <div className="flex items-center gap-2">
                      <Receipt className="h-4 w-4 text-accent shrink-0" aria-hidden />
                      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                        {t('settings.dealership.branch.form.sectionFiscal')}
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed max-w-3xl">
                      {t('settings.dealership.branch.form.fiscalHint')}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <ModernInput
                        className={INPUT_DS}
                        label={t('settings.dealership.branch.form.fields.stateRegistration')}
                        value={branchForm.stateRegistration}
                        onChange={e => setBranchForm(f => ({ ...f, stateRegistration: e.target.value }))}
                        disabled={isLoadingZipCode}
                      />
                      <ModernInput
                        className={INPUT_DS}
                        label={t('settings.dealership.branch.form.fields.taxRegime')}
                        value={branchForm.taxRegime}
                        onChange={e => setBranchForm(f => ({ ...f, taxRegime: e.target.value }))}
                        disabled={isLoadingZipCode}
                      />
                      <ModernInput
                        className={INPUT_DS}
                        label={t('settings.dealership.branch.form.fields.fiscalDefaultNcm')}
                        value={branchForm.fiscalDefaultNcm}
                        onChange={e => setBranchForm(f => ({ ...f, fiscalDefaultNcm: e.target.value }))}
                        disabled={isLoadingZipCode}
                      />
                      <ModernInput
                        className={INPUT_DS}
                        label={t('settings.dealership.branch.form.fields.fiscalDefaultCfop')}
                        value={branchForm.fiscalDefaultCfop}
                        onChange={e => setBranchForm(f => ({ ...f, fiscalDefaultCfop: e.target.value }))}
                        disabled={isLoadingZipCode}
                      />
                    </div>
                  </div>

                  <div className="space-y-3 pt-2 border-t border-border">
                    <p className="text-sm font-semibold text-foreground">{t('settings.dealership.branch.form.categoriesSection')}</p>
                    <p className="text-xs text-muted-foreground">{t('settings.dealership.branch.form.categoriesHint')}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {VEHICLE_CATEGORY_DEFS.map(cat => {
                        const selected = (branchForm.allowedVehicleCategories || []).includes(cat.value)
                        return (
                          <button
                            key={cat.value}
                            type="button"
                            disabled={isLoadingZipCode}
                            onClick={() => toggleFormCategory(cat.value)}
                            className={[
                              'flex items-center gap-3 p-4 rounded-xl border text-left text-sm transition-all duration-200',
                              'shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
                              'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-card disabled:hover:border-border',
                              selected
                                ? 'border-accent bg-accent/5 ring-1 ring-accent/20'
                                : 'border-border bg-card hover:border-accent/40 hover:bg-surface/50',
                            ].join(' ')}
                          >
                            <span className="text-xl leading-none" aria-hidden>
                              {cat.icon}
                            </span>
                            <span className="font-medium text-foreground">{t(cat.labelKey)}</span>
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  {branchOpError && <p className="text-sm text-destructive">{branchOpError}</p>}

                  <div className="flex flex-wrap items-center gap-3 pt-2">
                    <ModernButton
                      type="button"
                      size="md"
                      className="rounded-xl"
                      disabled={isLoadingZipCode}
                      onClick={handleBranchSubmit}
                    >
                      {editingBranchId ? t('settings.dealership.branch.form.saveEditButton') : t('settings.dealership.branch.form.createButton')}
                    </ModernButton>
                    <ModernButton
                      type="button"
                      variant="outline"
                      size="md"
                      className="rounded-xl"
                      disabled={isLoadingZipCode}
                      onClick={cancelBranchForm}
                    >
                      {t('actions.cancel')}
                    </ModernButton>
                  </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Error state */}
            {branchError && (
              <p className="text-sm text-destructive">{branchError}</p>
            )}

            {/* Loading state */}
            {loadingBranches && (
              <div className="flex items-center justify-center py-10">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent" />
              </div>
            )}

            {/* Empty state */}
            {!loadingBranches && branches.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-sm font-medium text-foreground">{t('settings.dealership.branch.emptyTitle')}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('settings.dealership.branch.emptyDescription')}
                </p>
              </div>
            )}

            {/* Branches list */}
            {!loadingBranches && branches.length > 0 && (
              <div className="space-y-3">
                {branches.map(branch => (
                  <Card key={branch.id}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0 space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-sm text-foreground">
                              {branch.name}
                            </span>
                            {branch.isPrimary && (
                              <Badge variant="primary">{t('settings.dealership.branch.primaryBadge')}</Badge>
                            )}
                            {branch.isActive ? (
                              <Badge variant="success">{t('common.active')}</Badge>
                            ) : (
                              <Badge variant="gray">{t('common.inactive')}</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                            <span>
                              {branch.street}, {branch.number}
                              {branch.complement ? `, ${branch.complement}` : ''} — {branch.neighborhood}, {branch.city}/{branch.state} · {formatCep(branch.zipCode || '')}
                            </span>
                          </div>
                          <div className="flex items-center gap-4">
                            {branch.phone && (
                              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Phone className="h-3.5 w-3.5" />
                                {branch.phone}
                              </span>
                            )}
                            {branch.email && (
                              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Mail className="h-3.5 w-3.5" />
                                {branch.email}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-1.5 pt-1">
                            <Car className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground mr-1">{t('settings.dealership.branch.listVehicleCategories')}:</span>
                            {normalizeBranchCategories(branch).map(code => (
                              <Badge key={code} variant="gray" className="text-[10px]">
                                {t(`settings.dealership.branch.categoryLabels.${code}` as const)}
                              </Badge>
                            ))}
                          </div>
                        </div>

                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => openEditBranchForm(branch)}
                            className="p-1.5 rounded-md text-muted-foreground hover:text-accent hover:bg-accent/10 transition-colors"
                            title="Editar"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          {!branch.isPrimary && (
                            <button
                              type="button"
                              onClick={() => handleDeleteBranch(branch)}
                              className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                              title="Remover"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
    </div>
  )
}
